/**
 * AudioPlayer
 * Based on AWS sample implementation
 * Manages audio playback using AudioWorklet for low-latency streaming
 */

export default class AudioPlayer {
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private analyser: AnalyserNode | null = null;
  private initialized: boolean = false;
  private onAudioPlayedListeners: Array<(samples: Float32Array) => void> = [];

  /**
   * Add event listener for audio playback events
   */
  addEventListener(event: string, callback: (samples: Float32Array) => void): void {
    if (event === 'onAudioPlayed') {
      this.onAudioPlayedListeners.push(callback);
    } else {
      console.error(`Listener registered for unsupported event type: ${event}`);
    }
  }

  /**
   * Initialize the audio player
   */
  async start(): Promise<void> {
    // Already running (a reconnect): keep playing what is buffered.
    if (this.initialized) return;
    try {
      // Create audio context with 24kHz sample rate (Nova Sonic output rate)
      this.audioContext = new AudioContext({ sampleRate: 24000 });

      // Create analyser for volume monitoring
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 512;

      // Load the AudioWorklet module
      // Create inline worklet to avoid bundling issues
      // Based on AWS sample's ExpandableBuffer approach
      const workletCode = `
        // Playback waits for a small cushion before it starts, so network
        // jitter does not chop the speech. It must never wait for audio that
        // is not coming: Sonic sends a reply in bursts, and the last burst (or
        // a short sentence like "Sure.") can be shorter than the cushion. So a
        // cushion that has stopped growing is played out as it is.
        const CUSHION_SAMPLES = 6000;      // 0.25 s at 24 kHz
        const STALLED_AFTER_SAMPLES = 3600; // 0.15 s with no new audio

        class ExpandableBuffer {
          constructor() {
            // Start with one second's worth of buffered audio capacity
            this.buffer = new Float32Array(24000);
            this.readIndex = 0;
            this.writeIndex = 0;
            this.underflowedSamples = 0;
            this.isInitialBuffering = true;
            this.initialBufferLength = CUSHION_SAMPLES;
            // Output frames rendered since audio last arrived.
            this.samplesSinceWrite = 0;
          }

          write(samples) {
            if (this.writeIndex + samples.length <= this.buffer.length) {
              // Enough space to append the new samples
            } else {
              // Not enough space ...
              if (samples.length <= this.readIndex) {
                // ... but we can shift samples to the beginning of the buffer
                const subarray = this.buffer.subarray(this.readIndex, this.writeIndex);
                this.buffer.set(subarray);
              } else {
                // ... and we need to grow the buffer capacity
                const newLength = (samples.length + this.writeIndex - this.readIndex) * 2;
                const newBuffer = new Float32Array(newLength);
                newBuffer.set(this.buffer.subarray(this.readIndex, this.writeIndex));
                this.buffer = newBuffer;
              }
              this.writeIndex -= this.readIndex;
              this.readIndex = 0;
            }
            this.buffer.set(samples, this.writeIndex);
            this.writeIndex += samples.length;
            this.samplesSinceWrite = 0;
            if (this.writeIndex - this.readIndex >= this.initialBufferLength) {
              // Filled the initial buffer length, so we can start playback with some cushion
              this.isInitialBuffering = false;
            }
          }

          read(destination) {
            let copyLength = 0;
            this.samplesSinceWrite += destination.length;
            if (
              this.isInitialBuffering &&
              this.writeIndex > this.readIndex &&
              this.samplesSinceWrite >= STALLED_AFTER_SAMPLES
            ) {
              // Nothing more is arriving: play what is buffered now.
              this.isInitialBuffering = false;
            }
            if (!this.isInitialBuffering) {
              // Only start to play audio after we've built up some initial cushion
              copyLength = Math.min(destination.length, this.writeIndex - this.readIndex);
            }
            destination.set(this.buffer.subarray(this.readIndex, this.readIndex + copyLength));
            this.readIndex += copyLength;
            if (copyLength < destination.length) {
              // Not enough samples (buffer underflow). Fill the rest with silence.
              destination.fill(0, copyLength);
              this.underflowedSamples += destination.length - copyLength;
            }
            if (copyLength === 0) {
              // Ran out of audio, so refill the buffer before playing more
              this.isInitialBuffering = true;
            }
          }

          clearBuffer() {
            this.readIndex = 0;
            this.writeIndex = 0;
            // The next reply builds its own cushion.
            this.isInitialBuffering = true;
          }
        }

        class AudioPlayerProcessor extends AudioWorkletProcessor {
          constructor() {
            super();
            this.playbackBuffer = new ExpandableBuffer();
            this.port.onmessage = (event) => {
              if (event.data.type === 'audio') {
                this.playbackBuffer.write(event.data.audioData);
              } else if (event.data.type === 'initial-buffer-length') {
                this.playbackBuffer.initialBufferLength = event.data.bufferLength;
              } else if (event.data.type === 'barge-in') {
                this.playbackBuffer.clearBuffer();
              }
            };
          }

          process(inputs, outputs, parameters) {
            const output = outputs[0][0]; // One output with one channel
            this.playbackBuffer.read(output);
            return true;
          }
        }

        registerProcessor('audio-player-processor', AudioPlayerProcessor);
      `;

      const blob = new Blob([workletCode], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);

      await this.audioContext.audioWorklet.addModule(workletUrl);
      URL.revokeObjectURL(workletUrl);

      // Create the worklet node
      this.workletNode = new AudioWorkletNode(
        this.audioContext,
        'audio-player-processor'
      );

      // Connect the audio graph
      this.workletNode.connect(this.analyser);
      this.analyser.connect(this.audioContext.destination);

      this.initialized = true;
      console.log('AudioPlayer initialized successfully');
    } catch (error) {
      console.error('Failed to initialize AudioPlayer:', error);
      throw error;
    }
  }

  /**
   * Stop and clean up the audio player
   */
  stop(): void {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    this.initialized = false;
    console.log('AudioPlayer stopped');
  }

  /**
   * Play audio samples
   * @param samples Float32Array of audio samples
   */
  playAudio(samples: Float32Array): void {
    if (!this.initialized || !this.workletNode) {
      console.error('AudioPlayer is not initialized. Call start() before playing audio.');
      return;
    }

    // Send audio data to the worklet
    this.workletNode.port.postMessage({
      type: 'audio',
      audioData: samples,
    });

    // Notify listeners
    this.onAudioPlayedListeners.forEach(listener => listener(samples));
  }

  /**
   * Clear the audio buffer (barge-in / interrupt)
   */
  bargeIn(): void {
    if (!this.initialized || !this.workletNode) {
      return;
    }

    this.workletNode.port.postMessage({
      type: 'barge-in',
    });
  }

  /**
   * Get current audio samples from the analyser
   */
  getSamples(): number[] | null {
    if (!this.initialized || !this.analyser) {
      return null;
    }

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteTimeDomainData(dataArray);

    return Array.from(dataArray).map(e => e / 128 - 1);
  }

  /**
   * Get current volume level
   */
  getVolume(): number {
    if (!this.initialized || !this.analyser) {
      return 0;
    }

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteTimeDomainData(dataArray);

    const normSamples = Array.from(dataArray).map(e => e / 128 - 1);
    let sum = 0;
    for (let i = 0; i < normSamples.length; i++) {
      sum += normSamples[i] * normSamples[i];
    }

    return Math.sqrt(sum / normSamples.length);
  }

  /**
   * Check if the player is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}
